/**
 * Country data for phone number input — emoji flag, ISO code, dial code.
 * Sorted alphabetically by country name.
 */

export interface CountryData {
  /** ISO 3166-1 alpha-2 code */
  iso2: string;
  /** Country name (English) */
  name: string;
  /** Dial code with leading + (e.g. "+44") */
  dialCode: string;
  /** Emoji flag */
  emoji: string;
}

/**
 * All countries with dial codes, sorted alphabetically by name.
 * Data is intentionally inlined (no npm dependency) for tree-shaking and bundle size.
 */
export const COUNTRIES: readonly CountryData[] = [
  { iso2: 'AF', name: 'Afghanistan', dialCode: '+93', emoji: '🇦🇫' },
  { iso2: 'AL', name: 'Albania', dialCode: '+355', emoji: '🇦🇱' },
  { iso2: 'DZ', name: 'Algeria', dialCode: '+213', emoji: '🇩🇿' },
  { iso2: 'AD', name: 'Andorra', dialCode: '+376', emoji: '🇦🇩' },
  { iso2: 'AO', name: 'Angola', dialCode: '+244', emoji: '🇦🇴' },
  { iso2: 'AG', name: 'Antigua and Barbuda', dialCode: '+1268', emoji: '🇦🇬' },
  { iso2: 'AR', name: 'Argentina', dialCode: '+54', emoji: '🇦🇷' },
  { iso2: 'AM', name: 'Armenia', dialCode: '+374', emoji: '🇦🇲' },
  { iso2: 'AU', name: 'Australia', dialCode: '+61', emoji: '🇦🇺' },
  { iso2: 'AT', name: 'Austria', dialCode: '+43', emoji: '🇦🇹' },
  { iso2: 'AZ', name: 'Azerbaijan', dialCode: '+994', emoji: '🇦🇿' },
  { iso2: 'BS', name: 'Bahamas', dialCode: '+1242', emoji: '🇧🇸' },
  { iso2: 'BH', name: 'Bahrain', dialCode: '+973', emoji: '🇧🇭' },
  { iso2: 'BD', name: 'Bangladesh', dialCode: '+880', emoji: '🇧🇩' },
  { iso2: 'BB', name: 'Barbados', dialCode: '+1246', emoji: '🇧🇧' },
  { iso2: 'BY', name: 'Belarus', dialCode: '+375', emoji: '🇧🇾' },
  { iso2: 'BE', name: 'Belgium', dialCode: '+32', emoji: '🇧🇪' },
  { iso2: 'BZ', name: 'Belize', dialCode: '+501', emoji: '🇧🇿' },
  { iso2: 'BJ', name: 'Benin', dialCode: '+229', emoji: '🇧🇯' },
  { iso2: 'BT', name: 'Bhutan', dialCode: '+975', emoji: '🇧🇹' },
  { iso2: 'BO', name: 'Bolivia', dialCode: '+591', emoji: '🇧🇴' },
  { iso2: 'BA', name: 'Bosnia and Herzegovina', dialCode: '+387', emoji: '🇧🇦' },
  { iso2: 'BW', name: 'Botswana', dialCode: '+267', emoji: '🇧🇼' },
  { iso2: 'BR', name: 'Brazil', dialCode: '+55', emoji: '🇧🇷' },
  { iso2: 'BN', name: 'Brunei', dialCode: '+673', emoji: '🇧🇳' },
  { iso2: 'BG', name: 'Bulgaria', dialCode: '+359', emoji: '🇧🇬' },
  { iso2: 'BF', name: 'Burkina Faso', dialCode: '+226', emoji: '🇧🇫' },
  { iso2: 'BI', name: 'Burundi', dialCode: '+257', emoji: '🇧🇮' },
  { iso2: 'CV', name: 'Cabo Verde', dialCode: '+238', emoji: '🇨🇻' },
  { iso2: 'KH', name: 'Cambodia', dialCode: '+855', emoji: '🇰🇭' },
  { iso2: 'CM', name: 'Cameroon', dialCode: '+237', emoji: '🇨🇲' },
  { iso2: 'CA', name: 'Canada', dialCode: '+1', emoji: '🇨🇦' },
  { iso2: 'CF', name: 'Central African Republic', dialCode: '+236', emoji: '🇨🇫' },
  { iso2: 'TD', name: 'Chad', dialCode: '+235', emoji: '🇹🇩' },
  { iso2: 'CL', name: 'Chile', dialCode: '+56', emoji: '🇨🇱' },
  { iso2: 'CN', name: 'China', dialCode: '+86', emoji: '🇨🇳' },
  { iso2: 'CO', name: 'Colombia', dialCode: '+57', emoji: '🇨🇴' },
  { iso2: 'KM', name: 'Comoros', dialCode: '+269', emoji: '🇰🇲' },
  { iso2: 'CG', name: 'Congo', dialCode: '+242', emoji: '🇨🇬' },
  { iso2: 'CD', name: 'Congo (DRC)', dialCode: '+243', emoji: '🇨🇩' },
  { iso2: 'CR', name: 'Costa Rica', dialCode: '+506', emoji: '🇨🇷' },
  { iso2: 'HR', name: 'Croatia', dialCode: '+385', emoji: '🇭🇷' },
  { iso2: 'CU', name: 'Cuba', dialCode: '+53', emoji: '🇨🇺' },
  { iso2: 'CY', name: 'Cyprus', dialCode: '+357', emoji: '🇨🇾' },
  { iso2: 'CZ', name: 'Czechia', dialCode: '+420', emoji: '🇨🇿' },
  { iso2: 'DK', name: 'Denmark', dialCode: '+45', emoji: '🇩🇰' },
  { iso2: 'DJ', name: 'Djibouti', dialCode: '+253', emoji: '🇩🇯' },
  { iso2: 'DM', name: 'Dominica', dialCode: '+1767', emoji: '🇩🇲' },
  { iso2: 'DO', name: 'Dominican Republic', dialCode: '+1809', emoji: '🇩🇴' },
  { iso2: 'EC', name: 'Ecuador', dialCode: '+593', emoji: '🇪🇨' },
  { iso2: 'EG', name: 'Egypt', dialCode: '+20', emoji: '🇪🇬' },
  { iso2: 'SV', name: 'El Salvador', dialCode: '+503', emoji: '🇸🇻' },
  { iso2: 'GQ', name: 'Equatorial Guinea', dialCode: '+240', emoji: '🇬🇶' },
  { iso2: 'ER', name: 'Eritrea', dialCode: '+291', emoji: '🇪🇷' },
  { iso2: 'EE', name: 'Estonia', dialCode: '+372', emoji: '🇪🇪' },
  { iso2: 'SZ', name: 'Eswatini', dialCode: '+268', emoji: '🇸🇿' },
  { iso2: 'ET', name: 'Ethiopia', dialCode: '+251', emoji: '🇪🇹' },
  { iso2: 'FJ', name: 'Fiji', dialCode: '+679', emoji: '🇫🇯' },
  { iso2: 'FI', name: 'Finland', dialCode: '+358', emoji: '🇫🇮' },
  { iso2: 'FR', name: 'France', dialCode: '+33', emoji: '🇫🇷' },
  { iso2: 'GA', name: 'Gabon', dialCode: '+241', emoji: '🇬🇦' },
  { iso2: 'GM', name: 'Gambia', dialCode: '+220', emoji: '🇬🇲' },
  { iso2: 'GE', name: 'Georgia', dialCode: '+995', emoji: '🇬🇪' },
  { iso2: 'DE', name: 'Germany', dialCode: '+49', emoji: '🇩🇪' },
  { iso2: 'GH', name: 'Ghana', dialCode: '+233', emoji: '🇬🇭' },
  { iso2: 'GR', name: 'Greece', dialCode: '+30', emoji: '🇬🇷' },
  { iso2: 'GD', name: 'Grenada', dialCode: '+1473', emoji: '🇬🇩' },
  { iso2: 'GT', name: 'Guatemala', dialCode: '+502', emoji: '🇬🇹' },
  { iso2: 'GN', name: 'Guinea', dialCode: '+224', emoji: '🇬🇳' },
  { iso2: 'GW', name: 'Guinea-Bissau', dialCode: '+245', emoji: '🇬🇼' },
  { iso2: 'GY', name: 'Guyana', dialCode: '+592', emoji: '🇬🇾' },
  { iso2: 'HT', name: 'Haiti', dialCode: '+509', emoji: '🇭🇹' },
  { iso2: 'HN', name: 'Honduras', dialCode: '+504', emoji: '🇭🇳' },
  { iso2: 'HK', name: 'Hong Kong', dialCode: '+852', emoji: '🇭🇰' },
  { iso2: 'HU', name: 'Hungary', dialCode: '+36', emoji: '🇭🇺' },
  { iso2: 'IS', name: 'Iceland', dialCode: '+354', emoji: '🇮🇸' },
  { iso2: 'IN', name: 'India', dialCode: '+91', emoji: '🇮🇳' },
  { iso2: 'ID', name: 'Indonesia', dialCode: '+62', emoji: '🇮🇩' },
  { iso2: 'IR', name: 'Iran', dialCode: '+98', emoji: '🇮🇷' },
  { iso2: 'IQ', name: 'Iraq', dialCode: '+964', emoji: '🇮🇶' },
  { iso2: 'IE', name: 'Ireland', dialCode: '+353', emoji: '🇮🇪' },
  { iso2: 'IL', name: 'Israel', dialCode: '+972', emoji: '🇮🇱' },
  { iso2: 'IT', name: 'Italy', dialCode: '+39', emoji: '🇮🇹' },
  { iso2: 'CI', name: 'Ivory Coast', dialCode: '+225', emoji: '🇨🇮' },
  { iso2: 'JM', name: 'Jamaica', dialCode: '+1876', emoji: '🇯🇲' },
  { iso2: 'JP', name: 'Japan', dialCode: '+81', emoji: '🇯🇵' },
  { iso2: 'JO', name: 'Jordan', dialCode: '+962', emoji: '🇯🇴' },
  { iso2: 'KZ', name: 'Kazakhstan', dialCode: '+7', emoji: '🇰🇿' },
  { iso2: 'KE', name: 'Kenya', dialCode: '+254', emoji: '🇰🇪' },
  { iso2: 'KI', name: 'Kiribati', dialCode: '+686', emoji: '🇰🇮' },
  { iso2: 'KW', name: 'Kuwait', dialCode: '+965', emoji: '🇰🇼' },
  { iso2: 'KG', name: 'Kyrgyzstan', dialCode: '+996', emoji: '🇰🇬' },
  { iso2: 'LA', name: 'Laos', dialCode: '+856', emoji: '🇱🇦' },
  { iso2: 'LV', name: 'Latvia', dialCode: '+371', emoji: '🇱🇻' },
  { iso2: 'LB', name: 'Lebanon', dialCode: '+961', emoji: '🇱🇧' },
  { iso2: 'LS', name: 'Lesotho', dialCode: '+266', emoji: '🇱🇸' },
  { iso2: 'LR', name: 'Liberia', dialCode: '+231', emoji: '🇱🇷' },
  { iso2: 'LY', name: 'Libya', dialCode: '+218', emoji: '🇱🇾' },
  { iso2: 'LI', name: 'Liechtenstein', dialCode: '+423', emoji: '🇱🇮' },
  { iso2: 'LT', name: 'Lithuania', dialCode: '+370', emoji: '🇱🇹' },
  { iso2: 'LU', name: 'Luxembourg', dialCode: '+352', emoji: '🇱🇺' },
  { iso2: 'MO', name: 'Macau', dialCode: '+853', emoji: '🇲🇴' },
  { iso2: 'MG', name: 'Madagascar', dialCode: '+261', emoji: '🇲🇬' },
  { iso2: 'MW', name: 'Malawi', dialCode: '+265', emoji: '🇲🇼' },
  { iso2: 'MY', name: 'Malaysia', dialCode: '+60', emoji: '🇲🇾' },
  { iso2: 'MV', name: 'Maldives', dialCode: '+960', emoji: '🇲🇻' },
  { iso2: 'ML', name: 'Mali', dialCode: '+223', emoji: '🇲🇱' },
  { iso2: 'MT', name: 'Malta', dialCode: '+356', emoji: '🇲🇹' },
  { iso2: 'MH', name: 'Marshall Islands', dialCode: '+692', emoji: '🇲🇭' },
  { iso2: 'MR', name: 'Mauritania', dialCode: '+222', emoji: '🇲🇷' },
  { iso2: 'MU', name: 'Mauritius', dialCode: '+230', emoji: '🇲🇺' },
  { iso2: 'MX', name: 'Mexico', dialCode: '+52', emoji: '🇲🇽' },
  { iso2: 'FM', name: 'Micronesia', dialCode: '+691', emoji: '🇫🇲' },
  { iso2: 'MD', name: 'Moldova', dialCode: '+373', emoji: '🇲🇩' },
  { iso2: 'MC', name: 'Monaco', dialCode: '+377', emoji: '🇲🇨' },
  { iso2: 'MN', name: 'Mongolia', dialCode: '+976', emoji: '🇲🇳' },
  { iso2: 'ME', name: 'Montenegro', dialCode: '+382', emoji: '🇲🇪' },
  { iso2: 'MA', name: 'Morocco', dialCode: '+212', emoji: '🇲🇦' },
  { iso2: 'MZ', name: 'Mozambique', dialCode: '+258', emoji: '🇲🇿' },
  { iso2: 'MM', name: 'Myanmar', dialCode: '+95', emoji: '🇲🇲' },
  { iso2: 'NA', name: 'Namibia', dialCode: '+264', emoji: '🇳🇦' },
  { iso2: 'NR', name: 'Nauru', dialCode: '+674', emoji: '🇳🇷' },
  { iso2: 'NP', name: 'Nepal', dialCode: '+977', emoji: '🇳🇵' },
  { iso2: 'NL', name: 'Netherlands', dialCode: '+31', emoji: '🇳🇱' },
  { iso2: 'NZ', name: 'New Zealand', dialCode: '+64', emoji: '🇳🇿' },
  { iso2: 'NI', name: 'Nicaragua', dialCode: '+505', emoji: '🇳🇮' },
  { iso2: 'NE', name: 'Niger', dialCode: '+227', emoji: '🇳🇪' },
  { iso2: 'NG', name: 'Nigeria', dialCode: '+234', emoji: '🇳🇬' },
  { iso2: 'KP', name: 'North Korea', dialCode: '+850', emoji: '🇰🇵' },
  { iso2: 'MK', name: 'North Macedonia', dialCode: '+389', emoji: '🇲🇰' },
  { iso2: 'NO', name: 'Norway', dialCode: '+47', emoji: '🇳🇴' },
  { iso2: 'OM', name: 'Oman', dialCode: '+968', emoji: '🇴🇲' },
  { iso2: 'PK', name: 'Pakistan', dialCode: '+92', emoji: '🇵🇰' },
  { iso2: 'PW', name: 'Palau', dialCode: '+680', emoji: '🇵🇼' },
  { iso2: 'PS', name: 'Palestine', dialCode: '+970', emoji: '🇵🇸' },
  { iso2: 'PA', name: 'Panama', dialCode: '+507', emoji: '🇵🇦' },
  { iso2: 'PG', name: 'Papua New Guinea', dialCode: '+675', emoji: '🇵🇬' },
  { iso2: 'PY', name: 'Paraguay', dialCode: '+595', emoji: '🇵🇾' },
  { iso2: 'PE', name: 'Peru', dialCode: '+51', emoji: '🇵🇪' },
  { iso2: 'PH', name: 'Philippines', dialCode: '+63', emoji: '🇵🇭' },
  { iso2: 'PL', name: 'Poland', dialCode: '+48', emoji: '🇵🇱' },
  { iso2: 'PT', name: 'Portugal', dialCode: '+351', emoji: '🇵🇹' },
  { iso2: 'QA', name: 'Qatar', dialCode: '+974', emoji: '🇶🇦' },
  { iso2: 'RO', name: 'Romania', dialCode: '+40', emoji: '🇷🇴' },
  { iso2: 'RU', name: 'Russia', dialCode: '+7', emoji: '🇷🇺' },
  { iso2: 'RW', name: 'Rwanda', dialCode: '+250', emoji: '🇷🇼' },
  { iso2: 'KN', name: 'Saint Kitts and Nevis', dialCode: '+1869', emoji: '🇰🇳' },
  { iso2: 'LC', name: 'Saint Lucia', dialCode: '+1758', emoji: '🇱🇨' },
  { iso2: 'VC', name: 'Saint Vincent', dialCode: '+1784', emoji: '🇻🇨' },
  { iso2: 'WS', name: 'Samoa', dialCode: '+685', emoji: '🇼🇸' },
  { iso2: 'SM', name: 'San Marino', dialCode: '+378', emoji: '🇸🇲' },
  { iso2: 'ST', name: 'Sao Tome and Principe', dialCode: '+239', emoji: '🇸🇹' },
  { iso2: 'SA', name: 'Saudi Arabia', dialCode: '+966', emoji: '🇸🇦' },
  { iso2: 'SN', name: 'Senegal', dialCode: '+221', emoji: '🇸🇳' },
  { iso2: 'RS', name: 'Serbia', dialCode: '+381', emoji: '🇷🇸' },
  { iso2: 'SC', name: 'Seychelles', dialCode: '+248', emoji: '🇸🇨' },
  { iso2: 'SL', name: 'Sierra Leone', dialCode: '+232', emoji: '🇸🇱' },
  { iso2: 'SG', name: 'Singapore', dialCode: '+65', emoji: '🇸🇬' },
  { iso2: 'SK', name: 'Slovakia', dialCode: '+421', emoji: '🇸🇰' },
  { iso2: 'SI', name: 'Slovenia', dialCode: '+386', emoji: '🇸🇮' },
  { iso2: 'SB', name: 'Solomon Islands', dialCode: '+677', emoji: '🇸🇧' },
  { iso2: 'SO', name: 'Somalia', dialCode: '+252', emoji: '🇸🇴' },
  { iso2: 'ZA', name: 'South Africa', dialCode: '+27', emoji: '🇿🇦' },
  { iso2: 'KR', name: 'South Korea', dialCode: '+82', emoji: '🇰🇷' },
  { iso2: 'SS', name: 'South Sudan', dialCode: '+211', emoji: '🇸🇸' },
  { iso2: 'ES', name: 'Spain', dialCode: '+34', emoji: '🇪🇸' },
  { iso2: 'LK', name: 'Sri Lanka', dialCode: '+94', emoji: '🇱🇰' },
  { iso2: 'SD', name: 'Sudan', dialCode: '+249', emoji: '🇸🇩' },
  { iso2: 'SR', name: 'Suriname', dialCode: '+597', emoji: '🇸🇷' },
  { iso2: 'SE', name: 'Sweden', dialCode: '+46', emoji: '🇸🇪' },
  { iso2: 'CH', name: 'Switzerland', dialCode: '+41', emoji: '🇨🇭' },
  { iso2: 'SY', name: 'Syria', dialCode: '+963', emoji: '🇸🇾' },
  { iso2: 'TW', name: 'Taiwan', dialCode: '+886', emoji: '🇹🇼' },
  { iso2: 'TJ', name: 'Tajikistan', dialCode: '+992', emoji: '🇹🇯' },
  { iso2: 'TZ', name: 'Tanzania', dialCode: '+255', emoji: '🇹🇿' },
  { iso2: 'TH', name: 'Thailand', dialCode: '+66', emoji: '🇹🇭' },
  { iso2: 'TL', name: 'Timor-Leste', dialCode: '+670', emoji: '🇹🇱' },
  { iso2: 'TG', name: 'Togo', dialCode: '+228', emoji: '🇹🇬' },
  { iso2: 'TO', name: 'Tonga', dialCode: '+676', emoji: '🇹🇴' },
  { iso2: 'TT', name: 'Trinidad and Tobago', dialCode: '+1868', emoji: '🇹🇹' },
  { iso2: 'TN', name: 'Tunisia', dialCode: '+216', emoji: '🇹🇳' },
  { iso2: 'TR', name: 'Turkey', dialCode: '+90', emoji: '🇹🇷' },
  { iso2: 'TM', name: 'Turkmenistan', dialCode: '+993', emoji: '🇹🇲' },
  { iso2: 'TV', name: 'Tuvalu', dialCode: '+688', emoji: '🇹🇻' },
  { iso2: 'UG', name: 'Uganda', dialCode: '+256', emoji: '🇺🇬' },
  { iso2: 'UA', name: 'Ukraine', dialCode: '+380', emoji: '🇺🇦' },
  { iso2: 'AE', name: 'United Arab Emirates', dialCode: '+971', emoji: '🇦🇪' },
  { iso2: 'GB', name: 'United Kingdom', dialCode: '+44', emoji: '🇬🇧' },
  { iso2: 'US', name: 'United States', dialCode: '+1', emoji: '🇺🇸' },
  { iso2: 'UY', name: 'Uruguay', dialCode: '+598', emoji: '🇺🇾' },
  { iso2: 'UZ', name: 'Uzbekistan', dialCode: '+998', emoji: '🇺🇿' },
  { iso2: 'VU', name: 'Vanuatu', dialCode: '+678', emoji: '🇻🇺' },
  { iso2: 'VA', name: 'Vatican City', dialCode: '+379', emoji: '🇻🇦' },
  { iso2: 'VE', name: 'Venezuela', dialCode: '+58', emoji: '🇻🇪' },
  { iso2: 'VN', name: 'Vietnam', dialCode: '+84', emoji: '🇻🇳' },
  { iso2: 'YE', name: 'Yemen', dialCode: '+967', emoji: '🇾🇪' },
  { iso2: 'ZM', name: 'Zambia', dialCode: '+260', emoji: '🇿🇲' },
  { iso2: 'ZW', name: 'Zimbabwe', dialCode: '+263', emoji: '🇿🇼' },
] as const;

/** Find a country by its ISO 3166-1 alpha-2 code. */
export function findCountryByIso2(iso2: string): CountryData | undefined {
  const upper = iso2.toUpperCase();
  return COUNTRIES.find((c) => c.iso2 === upper);
}

/**
 * Find the best matching country for a dial code string (e.g. "+44").
 * Prefers longer matches (e.g. "+1868" for Trinidad over "+1" for US).
 */
export function findCountryByDialCode(dialCode: string): CountryData | undefined {
  const code = dialCode.startsWith('+') ? dialCode : `+${dialCode}`;
  let bestMatch: CountryData | undefined;
  let bestLen = 0;
  for (const country of COUNTRIES) {
    if (code.startsWith(country.dialCode) && country.dialCode.length > bestLen) {
      bestMatch = country;
      bestLen = country.dialCode.length;
    }
  }
  return bestMatch;
}
